import { useCallback } from "react";
import { COUNTRY_CONFIG } from "../constants";

/**
 * Hook que encapsula funciones de formateo de moneda y localización.
 *
 * @param {string} pais - País de la sesión ("Chile" | "Perú")
 * @param {boolean} verEnDolares - Si el usuario activó "ver en dólares"
 * @param {boolean} showInDollars - Prop de control externo para mostrar en USD
 * @param {number[]} dolarMensual - Array de 12 valores de dólar mensual
 * @param {number} monthsToShow - Cantidad de meses a mostrar (según año)
 * @param {{ Chile: number, Perú: number }} dolarFallback - Último valor conocido por país
 */
export const useERFormatting = (pais, verEnDolares, showInDollars, dolarMensual, monthsToShow, dolarFallback) => {
  const localString = useCallback((p) => COUNTRY_CONFIG[p]?.locale || "es-CL", []);

  const moneda = useCallback((p) => COUNTRY_CONFIG[p]?.moneda || "$ ", []);

  const getMoneda = useCallback(() => (showInDollars ? "US$ " : moneda(pais)), [showInDollars, pais, moneda]);

  const formatCurrency = useCallback(
    (value, monthIndex = null, valoresMensuales = null, yaEnUSD = false) => {
      if (value === null || value === undefined || isNaN(value)) return "-";

      const locale = verEnDolares ? "es-CL" : pais === "Perú" ? "en-US" : "es-CL";
      const currency = verEnDolares ? "US$ " : moneda(pais);

      let finalValue = Math.abs(value);
      let isNegative = value < 0;

      // Si yaEnUSD=true, el valor ya viene en USD - no convertir
      if (yaEnUSD) {
        finalValue = Math.abs(value);
        isNegative = value < 0;
      } else if (verEnDolares) {
        if (monthIndex !== null && dolarMensual[monthIndex]) {
          // Mes específico: usa el tipo de cambio de ese mes
          finalValue = Math.abs(value) / dolarMensual[monthIndex];
        } else if (valoresMensuales && Array.isArray(valoresMensuales)) {
          // YTD: suma cada mes ya convertido individualmente (preservando signo)
          let sumaDolares = 0;
          valoresMensuales.slice(0, monthsToShow).forEach((v, i) => {
            if (v !== null && v !== undefined && dolarMensual[i]) {
              sumaDolares += v / dolarMensual[i]; // Preserva el signo
            }
          });
          finalValue = Math.abs(sumaDolares);
          isNegative = sumaDolares < 0;
        } else {
          // Fallback: usa promedio (para compatibilidad)
          const dolaresValidos = dolarMensual.slice(0, monthsToShow).filter((d) => d > 0);
          const promedioDolar =
            dolaresValidos.length > 0
              ? dolaresValidos.reduce((a, b) => a + b, 0) / dolaresValidos.length
              : (dolarFallback && dolarFallback[pais]) || 1;
          finalValue = Math.abs(value) / promedioDolar;
        }
      }

      // Mostrar 1 decimal solo si la cifra tiene un solo dígito entero (< 10)
      const fractionDigits = finalValue > 0 && finalValue < 10 ? 1 : 0;
      const formatted = finalValue.toLocaleString(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });

      // Negativos con paréntesis (convención contable)
      return isNegative ? `(${currency}${formatted})` : `${currency}${formatted}`;
    },
    [pais, moneda, verEnDolares, dolarMensual, monthsToShow, dolarFallback]
  );

  const formatFee = useCallback(
    (value, monedaParam) => {
      if (value === null || value === undefined || isNaN(value)) return "-";

      const absValue = Math.abs(value);
      const locale = localString(pais);
      const formatted = absValue.toLocaleString(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      return `${formatted} ${monedaParam}`;
    },
    [pais, localString]
  );

  return {
    localString,
    moneda,
    getMoneda,
    formatCurrency,
    formatFee,
  };
};
